import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signOut,
  confirmSignIn,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { CognitoConfig } from '../config/amplify';

Amplify.configure(CognitoConfig);

interface AuthUser {
  username: string;
  email?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * The Cognito ID token (not the access token) - API Gateway's Cognito authorizer reads
   * `cognito:groups` from this token's claims, which the access token does not carry.
   */
  idToken: string | null;
  signIn: (username: string, password: string) => Promise<void>;
  confirmMfa: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ID tokens are valid for 1 hour (see infra ArmWebClient); refresh proactively so a long-idle
// tab doesn't start failing API calls with a stale token.
const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString() ?? null;

      if (token) {
        setUser({
          username: currentUser.username,
          email: currentUser.signInDetails?.loginId,
        });
        setIdToken(token);
      } else {
        setUser(null);
        setIdToken(null);
      }
    } catch {
      setUser(null);
      setIdToken(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
    const interval = setInterval(refreshSession, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refreshSession]);

  const handleSignIn = async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await signIn({ username, password });
      if (result.isSignedIn) {
        await refreshSession();
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        throw new Error('MFA_REQUIRED');
      } else {
        throw new Error(`Unsupported sign-in step: ${result.nextStep.signInStep}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmMfa = async (code: string) => {
    setIsLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) {
        await refreshSession();
      } else {
        throw new Error(`Unsupported sign-in step: ${result.nextStep.signInStep}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await signOut({ global: true });
      setUser(null);
      setIdToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!idToken,
    isLoading,
    idToken,
    signIn: handleSignIn,
    confirmMfa: handleConfirmMfa,
    signOut: handleSignOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
